// We're using * imports because we're using commonjs and ts-node to download these.
import PrismicDom, { HTMLSerializer } from "prismic-dom";
/// <reference types="prismic-dom" />

import { debug as d } from "debug";
import uiConfig from "../../ui-config";
import { specificUrlMap } from "../../router/specific-cms-pages";
import {
  embedLookup,
  CmsDocumentTypeMap,
  CmsDocumentTypes,
  CmsRichText,
  CmsDocument,
  CmsLinkResource,
  webAppUrlMap,
  CmsLink,
  CmsMediaLink,
  globalPlanStructure,
  CmsDevelopPlan,
  CmsStrengthItem,
  CmsDevelopPage,
  CmsResource,
  CmsResourcePlaylist,
  CmsCarousel,
  CmsInfoPage,
  CmsQuote,
} from "./cms-type-definitions";
import ICmsStore from "./i-cms-store";
import CmsRemoteStore from "./cms-remote-store";
import { CmsVariableProvider } from "./cms-variable-provider";
import CmsImagePreloader from "./cms-image-preloader";
import enablePrismicToolbar from "./prismic-toolbar";

import { DevelopPlan } from "./develop-plan";
import { DevelopQuestion } from "./develop-question";
import { DevelopStep } from "./develop-step";

const debug = d("cs.CmsService");

// https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
function getParameterByName(name: string, specificUrl?: string) {
  const url = specificUrl ?? window.location.href;
  const sanitizedName = name.replace(/[[]]/g, "\\$&");
  const regex = new RegExp(`[?&]${sanitizedName}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

export declare type CmsResourceLink = {
  id: string;
  uid?: string;
  type: CmsDocumentTypes;
  isBroken?: boolean;
};

async function toPromise<T>(x: T) {
  return x;
}

export type CmsLinkResolver = (
  link: CmsResourceLink | CmsLink | CmsMediaLink | undefined,
) => string;

function webAppLinkResolver(link: { id: string; uid?: string; type: string }) {
  if (!link || !link.type || link.type === "broken_type") {
    return "";
  }

  const { id } = link;
  const uid = link.uid || "";
  const { type } = link;

  if (type === CmsDocumentTypes.CmsLinkResource && !!(link as CmsLinkResource).data) {
    const linkResource = link as CmsLinkResource;
    return linkResource.data.link_url || linkResource.data.link?.url;
  }

  let route = specificUrlMap[`${type}/${uid}`]?.root;

  if (!route) {
    route = webAppUrlMap[type];

    if (route === null) {
      throw new Error(`linkResolver: no route defined for type '${type}' for doc ${uid} ${id}`);
    }

    if (route === undefined) {
      throw new Error(`linkResolver: unknown type '${type}' for doc ${uid} ${id}`);
    }
  } else {
    return route;
  }

  return `${route}/${uid || id}`.replace("//", "/");
}

function nameof<T>(key: keyof T, instance?: T): keyof T {
  return key;
}

export default class CmsService {
  cmsStore: ICmsStore = new CmsRemoteStore();

  cmsVariableProvider: CmsVariableProvider = new CmsVariableProvider();

  cmsImagePreloader: CmsImagePreloader = new CmsImagePreloader();

  previewMode = uiConfig.isInPreviewMode;

  cache: { [x: string]: Promise<CmsDocument | null> | undefined } = {};

  async prepareAndCache(
    promise: Promise<CmsDocument | null>,
    type: string,
    idOrUid: string,
  ): Promise<CmsDocument | null> {
    const prepared = promise.then((doc) => this.prepare(doc));
    this.cache[`${type}/${idOrUid}`] = prepared;
    const doc = await prepared;

    if (doc) {
      this.cache[`${type}/${doc.id}`] = prepared;
      this.cache[`${type}/${doc.uid}`] = prepared;
    }

    return doc;
  }

  async fromCache(type: string, idOrUid: string): Promise<CmsDocument | null | undefined> {
    if (this.previewMode) {
      return undefined;
    }

    const promise = this.cache[`${type}/${idOrUid}`];
    if (promise) {
      return promise;
    }
    return undefined;
  }

  // typing on PrismicDom.HtmlSerializer is wrong or out of date. element typing can be an object.
  htmlSerializer = (
    type: string,
    element: Record<string, any>,
    text: string | null,
    children: string[],
    index: number,
    csVariableContextOverrides?: Record<string, string>,
  ): string => {
    // Don't wrap team-effectiveness in a <p> tag
    // if (element.type == "image") {
    //   return '<img src="' + element.url + '" alt="' + element.alt + '">';
    // }

    // Add a class to hyperlinks
    if (type === "hyperlink" && element.data.type === "cms-variable") {
      const replacementText = this.cmsVariableProvider.replaceText(
        element.data.uid,
        element.data.lang,
        csVariableContextOverrides,
      );
      return `<span>${replacementText}</span>`;
      // return null;
      // return '<a class="some-link" href="' + element.url + '">' + content + "</a>";
    }

    if (type === "hyperlink" && element.data.type === "cms-smart-callout") {
      return `<CmsSmartCallout>${element.data.uid}</CmsSmartCallout>`;
    }

    if (type === "hyperlink" && element.data.type === "cms-smart-text-box") {
      return `<CmsSmartTextBox>${element.data.uid}</CmsSmartTextBox>`;
    }

    // Handle 'blank' paragraphs consistently:
    // a single non-breaking space that causes a laid out paragraph with margins
    if (
      type === "paragraph" &&
      typeof element.text === "string" &&
      element.text.length <= 1 &&
      element.text.trim() === "" &&
      element.text.charCodeAt(0) !== 160
    ) {
      element.text = String.fromCharCode(160);
    }

    // Return null to stick with the default behavior
    return null as any as string; // We do this because the Prismic types seem to be messed up
  };

  getHtmlSerializer(csVariableContextOverrides?: Record<string, any>): typeof HTMLSerializer {
    const serializer: typeof HTMLSerializer = (
      type: string,
      element: any,
      text: string | null,
      children: string[],
      index: number,
    ): string => {
      return this.htmlSerializer(type, element, text, children, index, csVariableContextOverrides);
    };
    return serializer;
  }

  linkResolver: CmsLinkResolver = (
    link: CmsDocument | CmsResourceLink | CmsLink | CmsMediaLink | undefined,
  ): string => {
    if (!link) {
      return "";
    }

    if ("url" in link && link.url) {
      return link.url;
    }

    return webAppLinkResolver(link as CmsResourceLink) as string;
  };

  asText(richText: CmsRichText | undefined | null | string): string {
    if (richText === undefined || richText === null) {
      return "";
    }
    if (typeof richText === "string") {
      return richText;
    }
    return PrismicDom.RichText.asText(richText);
  }

  asHtml(
    richText: CmsRichText | undefined | null | string,
    csVariableContextOverrides?: Record<string, string>,
  ): string {
    if (richText === undefined || richText === null) {
      return "";
    }

    if (typeof richText === "string") {
      // https://stackoverflow.com/questions/14129953/how-to-encode-a-string-in-javascript-for-displaying-in-html
      return richText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    let html = PrismicDom.RichText.asHtml(
      richText,
      this.linkResolver,
      this.getHtmlSerializer(csVariableContextOverrides),
    ) as string;

    html = html.replace(/<a/g, "<a class='link external' "); // Make external links work;

    // We distinguish between i and em, but prismic does not.
    html = html.replace(/<em\w*>/g, "<i>");
    html = html.replace(/<\/em\w*>/g, "</i>");
    return html;
  }

  async getById<X extends keyof CmsDocumentTypeMap>(
    type: X,
    id: string,
  ): Promise<CmsDocumentTypeMap[X]> {
    if (typeof type !== "string" || typeof id !== "string" || !id || !type) {
      throw new Error(
        `CmsService.getById(${JSON.stringify(type)}, ${JSON.stringify(id)}) has bad arguments`,
      );
    }

    let doc = await this.fromCache(type, id);

    if (!doc) {
      doc = await this.prepareAndCache(this.cmsStore.getById(id), type, id);
    }

    if (!doc) {
      throw new Error(`doc with id ${id} not found`);
    } else if (doc.type !== type) {
      throw new Error(`${doc!.id} is not a ${type}`);
    } else {
      return doc as CmsDocumentTypeMap[X];
    }
  }

  async getByUid<X extends keyof CmsDocumentTypeMap>(
    type: X,
    rawUid: string,
  ): Promise<CmsDocumentTypeMap[X]> {
    if (typeof type !== "string" || typeof rawUid !== "string" || !rawUid || !type) {
      throw new Error(
        `CmsService.getByUid(${JSON.stringify(type)}, ${JSON.stringify(rawUid)}) has bad arguments`,
      );
    }

    const uid = rawUid.toLowerCase(); // Implement case insensitivity.

    let doc = await this.fromCache(type, uid);

    if (!doc) {
      doc = await this.prepareAndCache(this.cmsStore.getByUid(type, uid), type, uid);
    }

    if (!doc) {
      throw new Error(`doc with type ${type} uid ${uid} not found`);
    } else if (doc.type !== type) {
      throw new Error(`${doc.id} is not a ${doc.type}, not a ${type}`);
    } else {
      return doc as CmsDocumentTypeMap[X];
    }
  }

  async getAll<X extends keyof CmsDocumentTypeMap>(type: X): Promise<CmsDocumentTypeMap[X][]> {
    const docs = await this.cmsStore.getAll(type);
    const awaits = docs.map((doc) =>
      this.prepareAndCache(toPromise(doc), doc.type, doc.uid || doc.id),
    );
    await Promise.all(awaits);
    return docs;
  }

  async getPlans(): Promise<CmsDevelopPlan[]> {
    return this.getAll(CmsDocumentTypes.CmsDevelopPlan);
  }

  async getDevelopPlans(): Promise<DevelopPlan[]> {
    debug("getDevelopPlans");
    const plans = await this.getPlans();
    const strengthItems = await this.getStrengthItems();
    const defaultQuestions = ["Q1", "Q2", "Q3", "Q4", "Q5", "Q6"].map(
      (q) => ({ wording: q, negativeScoring: false } as DevelopQuestion),
    );

    const dplans = plans.map((plan) => {
      const steps: DevelopStep[] = [];

      globalPlanStructure.forEach((week) => {
        week.days.forEach((step) => {
          const s: DevelopStep = {
            cmsPageId: plan.data[step.cmsDayMember].id,
            cmsPlanId: plan.id,
            stepRole: step.stepRole,
            dayNumber: step.dayNumber,
            weekNumber: week.weekNumber,
          };

          steps.push(s);
        });
      });

      const strengthItemForPlan = strengthItems.find(
        (si) => si.data.strength_item_id === plan.data.strength_item_id,
      );

      let questions = defaultQuestions;
      if (!strengthItemForPlan) {
        debug(`Strength item not available for plan ID ${plan.id}, UID ${plan.uid}`);
      } else if (strengthItemForPlan.data.questions.length !== 6) {
        debug(`Questions not defined for ${plan.data.strength_item_id}`);
      } else {
        questions = strengthItemForPlan.data.questions.map((x) => ({
          wording: this.asText(x.wording),
          negativeScoring: x.scoring === "negative",
        }));
      }

      return {
        cmsPlanId: plan.id,
        cmsPlanUid: plan.uid,
        strengthItemId: plan.data.strength_item_id,
        steps,
        questions,
        cmsPlan: plan.data,
      } as DevelopPlan;
    });

    debug("Develop plans: ", dplans);
    return dplans;
  }

  async getStrengthItems(): Promise<CmsStrengthItem[]> {
    return this.getAll(CmsDocumentTypes.CmsStrengthItem);
  }

  async getStrengthItemBySiId(strengthItemId: string): Promise<CmsStrengthItem | undefined> {
    return (await this.getStrengthItems()).find((i) => i.data.strength_item_id === strengthItemId);
  }

  async getPlan(id: string): Promise<CmsDevelopPlan> {
    return this.getById(CmsDocumentTypes.CmsDevelopPlan, id);
  }

  async getPage(
    type: typeof CmsDocumentTypes.CmsDevelopPage | typeof CmsDocumentTypes.CmsResource,
    id: string,
  ): Promise<CmsDevelopPage | CmsResource> {
    return this.cmsImagePreloader.preloadImagesForPage(this.getById(type, id));
  }

  public static developLibraryId = "XqG_URUAACQAFw_-";

  async getLibrary(): Promise<CmsResourcePlaylist> {
    return this.getById(CmsDocumentTypes.CmsResourcePlaylist, CmsService.developLibraryId);
  }

  async getCarousel(id: string): Promise<CmsCarousel> {
    return this.cmsImagePreloader.preloadImagesForCarousel(
      this.getById(CmsDocumentTypes.CmsCarousel, id),
    );
  }

  async getInfoPage(id: string): Promise<CmsInfoPage> {
    return this.getById(CmsDocumentTypes.CmsInfoPage, id);
  }

  async getQuote(id: string): Promise<CmsQuote> {
    return this.getById(CmsDocumentTypes.CmsQuote, id);
  }

  get previewUrl(): string | null {
    return getParameterByName("previewUrl");
  }

  async prepare(doc: CmsDocument | null): Promise<CmsDocument | null> {
    if (!doc) {
      return null;
    }

    const awaits = embedLookup[doc.type](doc as any).map((link) => {
      if (!link || !link.type) {
        return undefined;
      }

      if (link.uid) {
        return this.getByUid(link.type, link.uid).then((embed) => {
          link.data = embed.data;
        });
      }

      return this.getById(link.type, link.id).then((embed) => {
        link.data = embed.data;
      });
    });

    await Promise.all(awaits);

    return doc;
  }

  // Prismic requires that a redirect be performed before previewing works. This method will handle that behaviour.
  async handlePreviewing(): Promise<void> {
    // Where the redirect will point to is specfied by the token.
    const previewToken = getParameterByName("token");
    const documentId = getParameterByName("documentId");
    const previewUrl = getParameterByName("previewUrl");
    const previewMode = getParameterByName("previewMode");

    if (previewToken || previewMode || previewUrl) {
      enablePrismicToolbar();
    }

    if (previewToken !== null && documentId !== null && previewUrl === null) {
      const api = await CmsRemoteStore.api;

      const doc = await api.getByID(documentId);

      if (!doc) {
        window.location.reload();
      }

      let url = webAppLinkResolver(doc);

      if (url?.includes("?")) {
        url += "&previewMode=true";
      } else {
        url += "?previewMode=true";
      }

      window.location.href = url;
    }
  }

  getRef(doc: CmsDocument): string | null {
    return getParameterByName("ref", doc.href);
  }
}
